import api from './apiService';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

const platformService = {
  // Get all platforms
  getAll: async () => {
    // Silent operation

    const response = await api.get('/platforms');

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'platformService', 'getAll');

    // Return the data directly (should be an array of platforms)
    return apiResponse.data || [];
  },

  // Get platform by ID
  getById: async (id) => {
    const response = await api.get(`/platforms/${id}`);

    // Parse the consistent API response
    const apiResponse = parseConsistentApiResponse(response, 'platformService', 'getById');

    // Return the data directly (should be a single platform object)
    return apiResponse.data;
  },

  // Create new platform
  create: async (platformData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('platformService: Creating platform with data:', platformData);
      }

      // Create a copy of the platformData to avoid modifying the original
      const platformDataToSend = { ...platformData };

      const response = await api.post('/platforms', platformDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'platformService', 'create');

      // Return the data directly (should be the created platform object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('platformService: Error creating platform');

      if (process.env.NODE_ENV === 'development') {
        console.error('platformService: Error details:', error);
        console.error('platformService: Error response data:', error.response?.data);
        console.error('platformService: Error status:', error.response?.status);
      }

      // Rethrow with more detailed error message
      if (error.response?.data) {
        const errorMessage = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        throw new Error(`API Error (${error.response.status}): ${errorMessage}`);
      }
      throw error;
    }
  },

  // Update platform
  update: async (id, platformData) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('platformService: Updating platform with ID:', id, 'and data:', platformData);
      }

      // Create a copy of the platformData to avoid modifying the original
      const platformDataToSend = { ...platformData };

      const response = await api.put(`/platforms/${id}`, platformDataToSend);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'platformService', 'update');

      // Return the data directly (should be the updated platform object)
      return apiResponse.data;
    } catch (error) {
      // Always log errors, even in production
      console.error('platformService: Error updating platform');

      if (process.env.NODE_ENV === 'development') {
        console.error('platformService: Error details:', error);
        console.error('platformService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },

  // Delete platform
  remove: async (id) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('platformService: Deleting platform with ID:', id);
      }

      const response = await api.delete(`/platforms/${id}`);

      // Parse the consistent API response
      const apiResponse = parseConsistentApiResponse(response, 'platformService', 'remove');

      // For delete operations, return the ID if successful
      if (apiResponse.success) {
        return id;
      }

      return apiResponse;
    } catch (error) {
      // Always log errors, even in production
      console.error('platformService: Error deleting platform');

      if (process.env.NODE_ENV === 'development') {
        console.error('platformService: Error details:', error);
        console.error('platformService: Error response data:', error.response?.data);
      }

      throw error;
    }
  },
};

export default platformService;
